import { onBeforeMount, ref } from 'vue'
import useAppStore from '@/h5/stores/app'
import { customRequest } from '@/h5/api/activity'
import { requestUnityApi } from '@/h5/utils/appActions/index'
import { isAPP } from '@/h5/utils/browserInfo'
import { requestAppEventApi } from '@/h5/plugin/appActionPlugin'
import { setParamsByDataSource } from '@/packages/plugins/hook/useDataSource'
import { emitClickEvents } from '@/packages/plugins/hook/useEvents'
import { randomNum, getTimestamp, getSignedData } from '@/packages/plugins/utils'
import config from '@/h5/config/index'
import { cloneDeep } from 'lodash'

const { baseSecret } = config
const useDataSource = () => {
  // 全局状态
  let appStore = null
  // 页面请求队列实例
  const pageReqInterval = ref({})
  // 请求接口映射
  const apiRequstMaps = {}

  /**
   * 请求数据
   * @param {*} config
   * @returns
   */
  const request = (config, customParams) => {
    let params = getDataSourceParams(config.requestParams, customParams)
    console.log('---request---', config)
    if (config.isSigned) {
      // 是否参数签名
      params = {
        ...params,
        sign: '',
        appId: 1004,
        timestamp: getTimestamp(),
        random: randomNum(6)
      }
      params.sign = getSignedData(params, baseSecret)
    }
    const headers = getDataSourceHeaders(config.requestHeaders)
    if (!params) {
      return Promise.reject(new Error('request params error'))
    }
    if (config.unity && isAPP) {
      // unity请求, 预览模式下不请求unity
      return requestUnityApi(config.unityParams[0].value, params)
    } else if (config.appEvent && isAPP) {
      // unity请求, 预览模式下不请求unity
      return requestAppEventApi(config.appEventName, params)
    } else if (!config.unity) {
      return customRequest(config.baseUrl, config.url, config.requestType, headers, params)
    } else {
      return Promise.reject(new Error('not support request'))
    }
  }

  /**
   * 获取请求头
   * @param {*} requestParams
   * @returns
   */
  const getDataSourceHeaders = (requestHeaders) => {
    if (!requestHeaders) return {}
    if (!appStore) {
      appStore = useAppStore()
    }
    let headers = {}
    requestHeaders.forEach((header) => {
      headers[header.key] = header.value
    })

    // 更新 headers 对象中的特定字段
    const appHeaders = appStore.commonHeaders || {}
    Object.keys(appHeaders).forEach((key) => {
      if (headers[key] !== undefined) {
        headers[key] = appHeaders[key] || headers[key]
      }
    })
    // 重置uid
    headers.uid && (headers.uid = appStore.uid || '')
    headers.language && (headers.language = appStore.requestLanguage || '')
    return headers
  }

  /**
   * 获取请求参数
   * @param {*} requestParams
   * @returns
   */
  const getDataSourceParams = (requestParams, customParams) => {
    let params = {}
    requestParams.forEach((param) => {
      params[param.key] = param.value
      // 自定义参数
      if (customParams && customParams[param.key]) {
        params[param.key] = customParams[param.key]
      }
    })
    // 重置uid
    if (!(typeof params.uid === 'object')) {
      params.uid && (params.uid = appStore.uid || '')
    }
    params.language && (params.language = appStore.requestLanguage || '')
    params = setParamsByDataSource(params, appStore.pageDataSource)
    return params
  }

  /**
   * 接口请求后的事件加载
   */
  const initPageDataSourceEvents = (config) => {
    if (config && config.events && config.events.length > 0) {
      emitClickEvents(config.events)
    }
  }

  /**
   * 自定义请求
   * @param {*} uuid
   */
  const customActionRequest = async (uuid, customParams) => {
    const config = apiRequstMaps[uuid]
    return request(config, customParams)
  }

  /**
   * 组件请求
   * @param {*} uuid
   * @param {*} typeConfig
   */
  const componentActionRequest = async (uuid, typeConfig, customParams) => {
    const config = cloneDeep(apiRequstMaps[uuid])
    if (!config) {
      return Promise.reject(new Error('not apiRequstMaps'))
    }
    if (typeConfig === 'component-node' && typeof customParams === 'object' && Object.keys(customParams).length > 0) {
      // 节点事件自定义请求, 如有自定义请求参数则使用自定义请求参数
      const res = await request(config, customParams)
      appStore.setPageDataSource(config.uuid, res.data || null)
      if (res) initPageDataSourceEvents(config)
    } else if (typeConfig === 'component-default' || (!config.infinite && config.cycleIndex === 1)) {
      const res = await request(config)
      appStore.setPageDataSource(config.uuid, res.data || null)
      // console.log('请求场景,组件请求，单次请求', config.uuid)
      if (res) initPageDataSourceEvents(config)
    } else if (!config.infinite && config.cycleIndex > 1) {
      // 请求场景,页面加载，多次请求
      let cycleIndex = config.cycleIndex - 1
      const reqInterval = setInterval(() => {
        request(config).then((res) => {
          appStore.setPageDataSource(config.uuid, res.data || null)
          initPageDataSourceEvents(config)
        })
        cycleIndex--
        if (cycleIndex === 0) {
          clearInterval(reqInterval)
        }
        // console.log('请求场景,组件请求，多次请求', config.uuid, cycleIndex)
      }, config.cycleTime)
    } else if (config.infinite) {
      // 请求场景,页面加载，无限循环请求
      if (pageReqInterval.value[config.uuid]) {
        clearInterval(pageReqInterval.value[config.uuid])
        pageReqInterval.value[config.uuid] = null
      }
      pageReqInterval.value[config.uuid] = setInterval(() => {
        request(config).then((res) => {
          appStore.setPageDataSource(config.uuid, res.data || null)
          initPageDataSourceEvents(config)
        })
        // console.log('请求场景,组件请求，无限循环请求', config.uuid)
      }, config.cycleTime)
    }
  }

  /**
   * 组件请求初始化
   * @param {*} config
   */
  const initComponentDataSource = async (config) => {
    if (!appStore) {
      appStore = useAppStore()
    }
    // 添加数据源配置
    if (!apiRequstMaps[config.uuid]) {
      apiRequstMaps[config.uuid] = config
    }
    // 判断是否需要初始化默认请求一次
    if (config?.defaultOne) {
      await componentActionRequest(config.uuid, 'component-default')
    }
  }

  /**
   * 初始化页面数据源设置
   * @param {*} config
   */
  const initPageDataSource = async (config) => {
    if (!appStore) {
      appStore = useAppStore()
    }
    // 添加数据源配置
    if (!apiRequstMaps[config.uuid]) {
      apiRequstMaps[config.uuid] = config
    }

    if (!config.infinite && config.cycleIndex === 1) {
      // 请求场景,页面加载
      const res = await request(config)
      appStore.setPageDataSource(config.uuid, res.data || null)
      // console.log('请求场景,页面加载，单次请求', config.uuid)
      if (res) initPageDataSourceEvents(config)
    } else if (!config.infinite && config.cycleIndex > 1) {
      // 请求场景,页面加载，多次请求
      let cycleIndex = config.cycleIndex - 1
      const reqInterval = setInterval(() => {
        request(config).then((res) => {
          appStore.setPageDataSource(config.uuid, res.data || null)
          initPageDataSourceEvents(config)
        })
        cycleIndex--
        if (cycleIndex === 0) {
          clearInterval(reqInterval)
        }
        // console.log('请求场景,页面加载，多次请求', config.uuid, cycleIndex)
      }, config.cycleTime)
    } else if (config.infinite) {
      // 请求场景,页面加载，无限循环请求
      if (pageReqInterval.value[config.uuid]) {
        clearInterval(pageReqInterval.value[config.uuid])
        pageReqInterval.value[config.uuid] = null
      }
      pageReqInterval.value[config.uuid] = setInterval(() => {
        request(config).then((res) => {
          appStore.setPageDataSource(config.uuid, res.data || null)
          initPageDataSourceEvents(config)
        })
        // console.log('请求场景,页面加载，无限循环请求', config.uuid)
      }, config.cycleTime)
    }
  }

  /**
   * 清除数据源请求定时器
   */
  const clearDataSourceInterval = () => {
    pageReqInterval.value &&
      Object.keys(pageReqInterval.value).forEach((key) => {
        clearInterval(pageReqInterval.value[key])
        pageReqInterval.value[key] = null
      })
  }

  onBeforeMount(() => {
    appStore = useAppStore()
  })

  return {
    componentActionRequest,
    getDataSourceParams,
    initPageDataSource,
    initComponentDataSource,
    clearDataSourceInterval,
    customActionRequest,
    getDataSourceHeaders,
    apiRequstMaps
  }
}
const golbalDataSourceSingle = useDataSource()
export default golbalDataSourceSingle
